/**
 * קיבוץ נוסעים לפי חדרים מתוך snapshot הזמנה (LDS / דינמי / Gemonix וכו׳).
 * ללא קריאות backend — ניסיון מקורות שטחיים בלבד על אובייקט order.
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/** @typedef {'adult' | 'child' | 'infant'} PaxCategory */

/**
 * @typedef {object} ResolvedRoomPassenger
 * @property {string} displayName
 * @property {string} birthDateRaw
 * @property {string} birthDateDisplay
 * @property {PaxCategory} category
 */

/**
 * @typedef {object} ResolvedPassengersRoom
 * @property {number} roomIndex — 1-based
 * @property {string} title — כותרת תצוגה (למשל "חדר 1 (DBL)")
 * @property {string} clipboardTitle — כותרת להעתקה ("חדר 1")
 * @property {number} adultsCount
 * @property {number} childrenCount
 * @property {number} infantsCount
 * @property {ResolvedRoomPassenger[]} passengers
 */

/**
 * @typedef {object} ResolveOrderPassengersByRoomsResult
 * @property {string | null} orderId
 * @property {ResolvedPassengersRoom[]} rooms
 * @property {boolean} fallbackUsed
 * @property {string} groupingSource
 * @property {number} totalPassengers
 * @property {number} groupedPassengersCount
 * @property {number} roomsDetected
 */

function safeJsonParseArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function getNested(obj, path) {
  if (!obj || typeof obj !== "object") return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

export function pickOrderPassengerBirthDateRaw(pax) {
  if (!pax || typeof pax !== "object") return "";
  for (const key of ["birthDate", "dateOfBirth", "date_of_birth", "age", "dob", "DOB"]) {
    const v = pax[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

export function buildOrderPassengerDisplayName(pax) {
  if (!pax || typeof pax !== "object") return "";
  const gemonixName = pax?.PersonDetails?.Name ?? pax?.personDetails?.name;
  if (gemonixName && typeof gemonixName === "object") {
    const prefix = String(gemonixName.NamePrefix ?? gemonixName.namePrefix ?? "").trim();
    const given = String(gemonixName.GivenName ?? gemonixName.givenName ?? "").trim();
    const surname = String(gemonixName.Surname ?? gemonixName.surname ?? "").trim();
    const gComposed = [prefix, given, surname].filter(Boolean).join(" ").trim();
    if (gComposed) return gComposed;
  }
  const title = String(pax.title ?? pax.Title ?? "").trim();
  const fn = String(
    pax.firstname ??
      pax.firstName ??
      pax.FirstName ??
      pax.first_name ??
      pax.givenName ??
      pax.given_name ??
      ""
  ).trim();
  const ln = String(
    pax.lastname ??
      pax.lastName ??
      pax.LastName ??
      pax.last_name ??
      pax.surname ??
      pax.familyName ??
      pax.family_name ??
      ""
  ).trim();
  const legacy = String(
    pax.name ?? pax.fullName ?? pax.FullName ?? pax.fullname ?? ""
  ).trim();
  const identity = `${fn} ${ln}`.replace(/\s+/g, " ").trim();
  if (identity) {
    return `${title} ${identity}`.replace(/\s+/g, " ").trim();
  }
  return legacy;
}

export function formatOrderPassengerDob(raw) {
  const src = String(raw ?? "").trim();
  if (!src) return "";
  const ddmmyyyy = src.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyy) return `${ddmmyyyy[1]}/${ddmmyyyy[2]}/${ddmmyyyy[3]}`;
  const iso = src.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const strictDmy = dayjs(src, "DD/MM/YYYY", true);
  if (strictDmy.isValid()) return strictDmy.format("DD/MM/YYYY");
  const loose = dayjs(src);
  if (loose.isValid()) return loose.format("DD/MM/YYYY");
  return src;
}

function countSlotObjects(slot, key) {
  const b = slot?.[key];
  if (!Array.isArray(b)) return 0;
  return b.filter((x) => x && typeof x === "object").length;
}

function isLdsStyleRoomSlot(group) {
  if (!group || typeof group !== "object" || Array.isArray(group)) return false;
  const hasBuckets = ["adults", "kids", "infants"].some((k) => Array.isArray(group[k]));
  const hasRoomMeta = group.room != null && typeof group.room === "object";
  return hasBuckets || hasRoomMeta;
}

function countNamedPassengersInSlots(slots) {
  if (!Array.isArray(slots)) return 0;
  return slots.reduce((acc, slot) => acc + extractPassengersFromLdsSlot(slot).length, 0);
}

function mergeNamedPassengersIntoSlots(occupancySlots, namedSlots) {
  const occ = Array.isArray(occupancySlots) ? occupancySlots : [];
  const named = Array.isArray(namedSlots) ? namedSlots : [];
  if (!occ.length) return named;
  if (!named.length) return occ;
  return occ.map((slot, i) => {
    const donor = named[i] && typeof named[i] === "object" ? named[i] : null;
    const next = slot && typeof slot === "object" ? { ...slot } : {};
    for (const key of ["adults", "kids", "infants"]) {
      const occList = Array.isArray(slot?.[key]) ? slot[key] : [];
      const namedList = Array.isArray(donor?.[key]) ? donor[key] : [];
      if (!occList.length && namedList.length) {
        next[key] = namedList;
        continue;
      }
      next[key] = occList.map((pax, j) => {
        if (buildOrderPassengerDisplayName(pax)) return pax;
        const src = namedList[j];
        if (!src || typeof src !== "object") return pax;
        return { ...(pax && typeof pax === "object" ? pax : {}), ...src };
      });
    }
    if (!next.room && donor?.room) next.room = donor.room;
    return next;
  });
}

function overlayFlatNamesOntoSlots(slots, flatRows) {
  if (!Array.isArray(slots) || !slots.length || !Array.isArray(flatRows) || !flatRows.length) {
    return slots;
  }
  let cursor = 0;
  return slots.map((slot) => {
    const next = slot && typeof slot === "object" ? { ...slot } : {};
    for (const key of ["adults", "kids", "infants"]) {
      const list = Array.isArray(slot?.[key]) ? slot[key] : [];
      next[key] = list.map((pax) => {
        if (buildOrderPassengerDisplayName(pax)) return pax;
        const donor = flatRows[cursor++];
        if (!donor) return pax;
        return {
          ...(pax && typeof pax === "object" ? pax : {}),
          firstname: donor.displayName,
          name: donor.displayName,
          ...(donor.birthDateRaw ? { birthDate: donor.birthDateRaw } : {}),
        };
      });
    }
    return next;
  });
}

/** כל המועמדים בסגנון passengersinfo (שורש / טיסות / clientsinfo). */
export function collectOrderPassengersInfoSlotCandidates(order) {
  if (!order || typeof order !== "object") return [];
  const rawCandidates = [
    order.passengersinfo,
    order.passengers_info,
    order.passengersInfo,
    getNested(order, "flights.passengersinfo"),
    getNested(order, "flights.passengers_info"),
    getNested(order, "flights.passengersInfo"),
    getNested(order, "clientsinfo.passengersinfo"),
    getNested(order, "clientsInfo.passengersinfo"),
    getNested(order, "clientsinfo.hrFlightPackage.passengersinfo"),
    getNested(order, "clientsInfo.hrFlightPackage.passengersinfo"),
    getNested(order, "extended.passengersinfo"),
    getNested(order, "extended.passengers_info"),
  ];
  const out = [];
  const seen = new Set();
  for (const c of rawCandidates) {
    const arr = safeJsonParseArray(c);
    if (!arr.length || !arr.some(isLdsStyleRoomSlot)) continue;
    const key = JSON.stringify(arr);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(arr);
  }
  return out;
}

/** מערך "חדרים" בסגנון passengersinfo — כולל טיפוס מחרוזת JSON */
export function normalizeOrderPassengersInfoSlots(order) {
  const candidates = collectOrderPassengersInfoSlotCandidates(order);
  if (!candidates.length) return [];

  let richest = candidates[0];
  let richestNamed = countNamedPassengersInSlots(richest);
  for (const c of candidates.slice(1)) {
    const named = countNamedPassengersInSlots(c);
    if (named > richestNamed) {
      richest = c;
      richestNamed = named;
    }
  }

  const occupancy = candidates[0];
  const merged =
    richestNamed > 0 && richest !== occupancy
      ? mergeNamedPassengersIntoSlots(occupancy, richest)
      : occupancy;

  if (countNamedPassengersInSlots(merged) > 0) return merged;

  const remainingNamed = candidates.find((c) => countNamedPassengersInSlots(c) > 0);
  if (remainingNamed) return mergeNamedPassengersIntoSlots(merged, remainingNamed);

  return merged;
}

/**
 * Choose the passengersinfo payload with the most real names (for persist).
 * Live form rooms win when they contain names.
 */
export function pickRichestPassengersInfoForPersist(orderRow, liveRoomsAndPaxInfo) {
  const live = safeJsonParseArray(liveRoomsAndPaxInfo);
  const liveNamed = live.some(isLdsStyleRoomSlot) ? countNamedPassengersInSlots(live) : 0;
  if (liveNamed > 0) return live;

  const fromOrder = normalizeOrderPassengersInfoSlots(orderRow);
  if (fromOrder.length) return fromOrder;
  if (live.length && live.some(isLdsStyleRoomSlot)) return live;
  return fromOrder;
}

function extractPassengersFromLdsSlot(slot) {
  /** @type {ResolvedRoomPassenger[]} */
  const passengers = [];
  const buckets = [
    ["adults", "adult"],
    ["kids", "child"],
    ["infants", "infant"],
  ];
  for (const [key, category] of buckets) {
    const bucket = slot[key];
    if (!Array.isArray(bucket)) continue;
    bucket.forEach((pax) => {
      const displayName = buildOrderPassengerDisplayName(pax);
      if (!displayName) return;
      const birthDateRaw = pickOrderPassengerBirthDateRaw(pax);
      passengers.push({
        displayName,
        birthDateRaw,
        birthDateDisplay: formatOrderPassengerDob(birthDateRaw),
        category,
      });
    });
  }
  return passengers;
}

function displayTitleForSlot(slot, roomIndex) {
  const rt =
    slot?.room?.roomType ??
    slot?.room?.RoomType ??
    slot?.roomType ??
    slot?.RoomType ??
    "";
  const rtStr = rt != null && String(rt).trim() ? String(rt).trim() : "";
  const base = `חדר ${roomIndex}`;
  return rtStr ? `${base} (${rtStr})` : base;
}

function buildRoomsFromPassengersInfoSlots(slots) {
  /** @type {ResolvedPassengersRoom[]} */
  const rooms = [];
  slots.forEach((slot, i) => {
    const roomIndex = i + 1;
    const passengers = extractPassengersFromLdsSlot(slot);
    rooms.push({
      roomIndex,
      title: displayTitleForSlot(slot, roomIndex),
      clipboardTitle: `חדר ${roomIndex}`,
      adultsCount: countSlotObjects(slot, "adults"),
      childrenCount: countSlotObjects(slot, "kids"),
      infantsCount: countSlotObjects(slot, "infants"),
      passengers,
    });
  });
  return rooms;
}

function pickRoomIndexFromPax(p) {
  if (!p || typeof p !== "object") return null;
  const keys = [
    "roomIndex",
    "room_index",
    "RoomIndex",
    "roomId",
    "room_id",
    "RoomId",
    "roomRef",
    "room_ref",
    "chosenPaxRef",
    "paxRef",
    "pax_ref",
    "PaxRoomIndex",
    "paxRoomIndex",
  ];
  for (const k of keys) {
    if (p[k] == null || p[k] === "") continue;
    const n = parseInt(String(p[k]), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function pushFlatPassengerFromObj(p, source, bucket) {
  if (!p || typeof p !== "object") return null;
  const displayName = buildOrderPassengerDisplayName(p);
  if (!displayName) return null;
  const birthDateRaw = pickOrderPassengerBirthDateRaw(p);
  let ri = pickRoomIndexFromPax(p);
  if (ri != null && ri >= 1) {
    // חלק מהמקורות משתמשים ב־1-based כבר
  } else if (ri === 0) {
    ri = 1;
  }
  return {
    displayName,
    birthDateRaw,
    birthDateDisplay: formatOrderPassengerDob(birthDateRaw),
    category: bucket,
    roomIndexHint: ri,
    source,
  };
}

function gatherFlatPassengerRows(order) {
  /** @type {ReturnType<typeof pushFlatPassengerFromObj>[]} */
  const rows = [];

  const passengersRaw = order?.passengers;
  const arrPass = safeJsonParseArray(passengersRaw);
  arrPass.forEach((p) => {
    const row = pushFlatPassengerFromObj(p, "order.passengers", "adult");
    if (row) rows.push(row);
  });

  const pinfoFlat = safeJsonParseArray(order?.passengers_info);
  if (
    pinfoFlat.length &&
    !pinfoFlat.some(isLdsStyleRoomSlot) &&
    pinfoFlat.every((x) => x && typeof x === "object" && !Array.isArray(x.adults))
  ) {
    pinfoFlat.forEach((p) => {
      const row = pushFlatPassengerFromObj(p, "order.passengers_info_flat", "adult");
      if (row) rows.push(row);
    });
  }

  const scanArrays = [
    order?.hotel_allocation,
    order?.hotelAllocation,
    getNested(order, "ourPrice.hotelAllocation"),
    getNested(order, "ourprice.hotelAllocation"),
    getNested(order, "ourPrice.customerQuoteOffer.passengers"),
    getNested(order, "ourprice.customerQuoteOffer.passengers"),
    getNested(order, "extended.passengers"),
    getNested(order, "extended.Passengers"),
    getNested(order, "flights.passengers"),
    getNested(order, "clientsinfo.passengers"),
    getNested(order, "clientsInfo.passengers"),
    getNested(order, "clientsinfo.hrFlightPackage.passengers"),
    getNested(order, "clientsInfo.hrFlightPackage.passengers"),
  ];
  scanArrays.forEach((block, bi) => {
    const list = safeJsonParseArray(block);
    list.forEach((p) => {
      const row = pushFlatPassengerFromObj(p, `snapshot[${bi}]`, "adult");
      if (row) rows.push(row);
    });
  });

  const selectedRooms = order?.selectedRooms ?? order?.rooms ?? order?.selectedRoom;
  const srList = Array.isArray(selectedRooms)
    ? selectedRooms
    : selectedRooms && typeof selectedRooms === "object"
      ? [selectedRooms]
      : [];
  srList.forEach((roomBlock, ri) => {
    if (!roomBlock || typeof roomBlock !== "object") return;
    ["passengers", "Passengers", "adults", "children", "infants"].forEach((k) => {
      const list = safeJsonParseArray(roomBlock[k]);
      const bucket =
        k === "infants" ? "infant" : k === "children" || k === "kids" ? "child" : "adult";
      list.forEach((p) => {
        const row = pushFlatPassengerFromObj(
          typeof p === "object" ? { ...p, roomIndex: p.roomIndex ?? ri + 1 } : p,
          "selectedRooms",
          bucket
        );
        if (row) rows.push(row);
      });
    });
  });

  return rows.filter(Boolean);
}

function normalizeFlatRowRoomIndex(hint) {
  if (hint == null || hint === "") return null;
  const ri = parseInt(String(hint), 10);
  if (!Number.isFinite(ri)) return null;
  if (ri < 1) return 1;
  return ri;
}

function buildRoomsFromFlatRowsWithHints(rows) {
  if (!rows.length) return null;
  const hasAnyHint = rows.some((r) => r.roomIndexHint != null);
  if (!hasAnyHint) return null;

  const map = new Map();
  rows.forEach((r) => {
    const riRaw = r.roomIndexHint != null ? r.roomIndexHint : 1;
    const ri = normalizeFlatRowRoomIndex(riRaw) ?? 1;
    if (!map.has(ri)) map.set(ri, []);
    map.get(ri).push(r);
  });
  if (map.size === 0) return null;

  /** @type {ResolvedPassengersRoom[]} */
  const rooms = [];
  const sortedKeys = [...map.keys()].sort((a, b) => a - b);
  sortedKeys.forEach((roomIndex) => {
    const plist = map.get(roomIndex) || [];
    const passengers = plist.map((r) => ({
      displayName: r.displayName,
      birthDateRaw: r.birthDateRaw,
      birthDateDisplay: r.birthDateDisplay,
      category: r.category,
    }));
    const adultsCount = passengers.filter((p) => p.category === "adult").length;
    const childrenCount = passengers.filter((p) => p.category === "child").length;
    const infantsCount = passengers.filter((p) => p.category === "infant").length;
    rooms.push({
      roomIndex,
      title: `חדר ${roomIndex}`,
      clipboardTitle: `חדר ${roomIndex}`,
      adultsCount,
      childrenCount,
      infantsCount,
      passengers,
    });
  });
  return rooms;
}

function singleFallbackRoom(passengers) {
  const adultsCount = passengers.filter((p) => p.category === "adult").length;
  const childrenCount = passengers.filter((p) => p.category === "child").length;
  const infantsCount = passengers.filter((p) => p.category === "infant").length;
  return [
    {
      roomIndex: 1,
      title: "חדר 1",
      clipboardTitle: "חדר 1",
      adultsCount,
      childrenCount,
      infantsCount,
      passengers,
    },
  ];
}

function flattenFlatRows(rows) {
  return rows.map((r) => ({
    displayName: r.displayName,
    birthDateRaw: r.birthDateRaw,
    birthDateDisplay: r.birthDateDisplay,
    category: r.category,
  }));
}

function orderIdFromRow(order) {
  if (!order || typeof order !== "object") return null;
  const raw = order.id ?? order.dealid ?? order.orderid;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {ResolveOrderPassengersByRoomsResult}
 */
export function resolveOrderPassengersByRooms(order) {
  const empty = /** @type {ResolveOrderPassengersByRoomsResult} */ ({
    orderId: orderIdFromRow(order),
    rooms: [],
    fallbackUsed: true,
    groupingSource: "none",
    totalPassengers: 0,
    groupedPassengersCount: 0,
    roomsDetected: 0,
  });

  if (!order || typeof order !== "object") return empty;

  const oid = orderIdFromRow(order);

  let slots = normalizeOrderPassengersInfoSlots(order);
  const flatRowsEarly = gatherFlatPassengerRows(order);
  if (slots.length > 0 && countNamedPassengersInSlots(slots) === 0 && flatRowsEarly.length) {
    slots = overlayFlatNamesOntoSlots(slots, flatRowsEarly);
  }
  if (slots.length > 0) {
    const rooms = buildRoomsFromPassengersInfoSlots(slots);
    const groupedPassengersCount = rooms.reduce((acc, r) => acc + r.passengers.length, 0);
    const bucketTotals = rooms.reduce(
      (acc, r) => ({
        a: acc.a + r.adultsCount,
        c: acc.c + r.childrenCount,
        i: acc.i + r.infantsCount,
      }),
      { a: 0, c: 0, i: 0 }
    );
    const totalPassengers =
      groupedPassengersCount > 0
        ? groupedPassengersCount
        : bucketTotals.a + bucketTotals.c + bucketTotals.i;

    return {
      orderId: oid,
      rooms,
      fallbackUsed: false,
      groupingSource: "passengersinfo_slots",
      totalPassengers,
      groupedPassengersCount,
      roomsDetected: rooms.length,
    };
  }

  const flatRows = flatRowsEarly;
  const hinted = buildRoomsFromFlatRowsWithHints(flatRows);
  if (hinted && hinted.length) {
    const groupedPassengersCount = hinted.reduce((acc, r) => acc + r.passengers.length, 0);
    return {
      orderId: oid,
      rooms: hinted,
      fallbackUsed: false,
      groupingSource: "flat_passengers_room_hints",
      totalPassengers: groupedPassengersCount,
      groupedPassengersCount,
      roomsDetected: hinted.length,
    };
  }

  const asPassengers = flattenFlatRows(flatRows);
  if (asPassengers.length > 0) {
    const rooms = singleFallbackRoom(asPassengers);
    const groupedPassengersCount = asPassengers.length;
    return {
      orderId: oid,
      rooms,
      fallbackUsed: true,
      groupingSource: "flat_passengers_single_bucket",
      totalPassengers: groupedPassengersCount,
      groupedPassengersCount,
      roomsDetected: 1,
    };
  }

  return {
    ...empty,
    orderId: oid,
    groupingSource: "none",
  };
}

/** Flat list of passenger display names for order list cards (deduped, order preserved). */
export function resolveOrderPassengerDisplayNames(order) {
  const res = resolveOrderPassengersByRooms(order);
  const seen = new Set();
  /** @type {string[]} */
  const names = [];
  for (const room of res.rooms) {
    for (const pax of room.passengers) {
      const name = String(pax?.displayName ?? "").trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

/**
 * טקסט להדבקה — פורמט כמו בדרישת המוצר
 * @param {ResolvedPassengersRoom[]} rooms
 */
export function buildOrderPassengersClipboardText(rooms) {
  if (!Array.isArray(rooms) || !rooms.length) return "";
  const blocks = [];
  rooms.forEach((room) => {
    const head = `${room.clipboardTitle ?? `חדר ${room.roomIndex}`}:`;
    const lines = room.passengers.map((p, i) => {
      const dob =
        p.birthDateDisplay && String(p.birthDateDisplay).trim()
          ? ` - ${p.birthDateDisplay}`
          : "";
      return `${i + 1}. ${p.displayName}${dob}`;
    });
    blocks.push([head, ...lines].join("\n"));
  });
  return blocks.join("\n\n").trim();
}

/**
 * טקסט להעתקה לחדר בודד
 * @param {ResolvedPassengersRoom} room
 */
export function buildSingleRoomPassengersClipboardText(room) {
  return buildOrderPassengersClipboardText([room]);
}
